import React, { useEffect, useCallback } from 'react';
import { useLocation, useParams } from '@tanstack/react-router';
import {
  errorBoundary, withInject, Pagination as PaginationManager, QueryParams,
} from '@piwikpro/platform';
import {
  Stack,
  withTranslation,
  Spinner,
  List,
  Text,
  Button,
  Pagination,
  Tooltip,
  formatDate,
} from '@piwikpro/ui-components';
import { ListPreference } from '@piwikpro/user-preferences';
import { RouterService, buildPaginatedPath } from '@piwikpro/router-crate';
import { SingleDigitalGatewayService } from '../../SingleDigitalGatewayService';
import { ISingleDigitalGatewayRequestLog } from '../../interfaces';

type RequestPart = 'request' | 'response';

const downloadTextFile = ({
  name,
  suffix,
  data,
}: {
  name: string
  suffix: RequestPart
  data: string
}) => {
  const filename = `${name}_${suffix}.txt`;
  const anchor = document.createElement('a');
  const blobData = new Blob([data], { type: 'text/plain' });

  anchor.setAttribute('href', window.URL.createObjectURL(blobData));
  anchor.setAttribute('download', filename);

  anchor.click();
};

export interface IRequestLogProps {
  singleDigitalGateway: SingleDigitalGatewayService
  t: (key: string, variables?: any) => string
  appId: string
  total: number
  isLoading: boolean
  failed: boolean
  requests: ISingleDigitalGatewayRequestLog[]
  userPreferences: any
  routerService: RouterService
  config: any
}

export const RequestLog: React.FC<IRequestLogProps> = ({
  singleDigitalGateway,
  t,
  total,
  appId,
  isLoading,
  failed,
  requests,
  routerService,
  config,
  userPreferences,
}) => {
  const params = useParams({ strict: false }) as Record<string, string | undefined>;
  const { pathname } = useLocation();

  const pagination = PaginationManager.fromQueryParams(
    [],
    0,
    params as QueryParams,
  );

  const { reportId } = params;

  useEffect(() => {
    const fetchData = async () => {
      const filters = { limit: pagination.limit, offset: pagination.offset };
      try {
        await singleDigitalGateway.getReportRequestLog({ appId, reportId: reportId!, filters });
        // eslint-disable-next-line no-empty
      } catch (err) { }
    };

    fetchData();
  }, [pagination.limit, pagination.offset]);

  const handleLimitChange = (newLimit: number) => {
    const listPreference = new ListPreference(userPreferences.load(), '');

    listPreference.limit = newLimit;
    userPreferences.persist(listPreference);
    routerService.push(buildPaginatedPath(pathname, 1, newLimit));
  };

  const handlePageChange = useCallback((newOffset: number): void => {
    const newPage = pagination.getPageFromOffset(newOffset);

    routerService.push(buildPaginatedPath(pathname, newPage, pagination.limit));
  }, [pagination.limit, pagination, pathname, routerService]);

  if (isLoading) {
    return <Spinner />;
  }

  const handleDownloadRequestDetails = async ({
    id,
    type,
  }: {
    id: string
    type: RequestPart
  }) => {
    try {
      const data = await singleDigitalGateway.getRequestDetails({
        appId,
        reportId,
        requestId: id,
        type,
      });

      downloadTextFile({
        name: id,
        suffix: type,
        data,
      });
      // eslint-disable-next-line no-empty
    } catch { }
  };

  const getRow = ({
    id, type, createdAt, httpStatus,
  }: ISingleDigitalGatewayRequestLog) => {
    const statusValue = httpStatus >= 400 ? (
      <Tooltip>
        <Text as="p" color="Red">{httpStatus}</Text>
      </Tooltip>
    ) : <Text as="p">{httpStatus}</Text>;

    const createdAtLabel = formatDate(new Date(createdAt), {
      format: {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      },
    });

    return (
      <List.Item
        key={id}
      >
        <Text as="p">{t(type === 'generate_sdg_id' ? 'platform:apps.details.sdg.request-log.generate-sdg-id' : 'platform:apps.details.sdg.request-log.report-upload')}</Text>
        <Text as="p">{createdAtLabel}</Text>
        {statusValue}
        <Button
          icon="download"
          text=".txt"
          appearance="simple"
          onClick={() => handleDownloadRequestDetails({ id, type: 'request' })}
        />
        <Button
          icon="download"
          text=".txt"
          appearance="simple"
          onClick={() => handleDownloadRequestDetails({ id, type: 'response' })}
        />
      </List.Item>
    );
  };

  return (
    <Stack vertical>
      <Stack.Item fill>
        <Text as="h3" size={16} weight={700} wrapType="noWrapAndCutText" dataId="Header.small">{t('apps.details.sdg.summary.request-log.header')}</Text>
      </Stack.Item>
      <Stack.Item fill>
        <List
          headerColor="gray"
          columns={[
            {
              name: t('apps.details.sdg.request-log.list.event'),
              size: 2,
              align: 'left',
            },
            {
              name: t('apps.details.sdg.request-log.list.date'),
              size: 2,
              align: 'left',
            },
            {
              name: t('apps.details.sdg.request-log.list.http-status'),
              size: 2,
              align: 'left',
            },
            {
              name: t('apps.details.sdg.request-log.list.request'),
              size: 1,
              align: 'left',
            },
            {
              name: t('apps.details.sdg.request-log.list.response'),
              size: 1,
              align: 'left',
            },
          ]}
          isLoading={isLoading}
          error={failed}
        >
          {
            requests.map(
              rowData => getRow(rowData),
            )
          }
        </List>
      </Stack.Item>
      <Stack.Item fill>
        <Pagination
          onLimitChange={handleLimitChange}
          limit={pagination.limit}
          total={total}
          limitOptions={config.get('PAGINATION_ITEMS_PER_PAGE_OPTIONS')}
          startIndex={pagination.offset}
          endIndex={pagination.offset + pagination.limit}
          onPageChange={(e: number) => handlePageChange(e)}
        />
      </Stack.Item>
    </Stack>
  );
};

export default withInject({
  config: 'config',
  singleDigitalGateway: 'SingleDigitalGatewayCrate.singleDigitalGateway',
  userPreferences: 'userPreferences',
  routerService: 'RouterCrate.router',
})(withTranslation(['administration', 'platform'])(errorBoundary(RequestLog as any)));
